import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import {
  getAdvertisements,
  getAdvertisementById,
  getAdvertisementStats,
  createAdvertisement,
  updateAdvertisement,
  deleteAdvertisement,
  type AdvertisementDetailResponse,
  type AdvertisementResponse,
  type CreateAdvertisementRequest,
  type UpdateAdvertisementRequest,
} from "../services/advertisement"

export const ADVERTISEMENT_QUERY_KEYS = {
  ADVERTISEMENTS_LIST: ["advertisements"] as const,
  ADVERTISEMENT_DETAIL: (id: string) => ["advertisements", id] as const,
  ADVERTISEMENT_STATS: (id: string, from?: string, to?: string) =>
    ["advertisements", id, "stats", from, to] as const,
  ADVERTISEMENT_STATS_PREFIX: (id: string) => ["advertisements", id, "stats"] as const,
}

export function useAdvertisements(params?: {
  per?: number
  keyword?: string
  chain_id?: string
  status?: string
}) {
  const queryResult = useInfiniteQuery({
    queryKey: [...ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENTS_LIST, params],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const requestParams = {
          ...params,
          page: pageParam,
        }

        const response = await getAdvertisements(requestParams)
        return {
          data: response.data,
          meta: response.meta,
        }
      } catch {
        return {
          data: [],
          meta: {
            current_page: 1,
            next_page: null,
            prev_page: null,
            total_pages: 0,
            total_count: 0,
          },
        }
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined
      const { current_page, total_pages } = lastPage.meta
      return current_page < total_pages ? current_page + 1 : undefined
    },
    initialPageParam: 1,
    staleTime: 30000,
  })

  const data =
    queryResult.data?.pages?.flatMap(
      (page: { data: AdvertisementResponse[] }) => page?.data ?? []
    ) ?? []
  const totalCount = queryResult.data?.pages?.at(-1)?.meta?.total_count ?? 0

  return {
    ...queryResult,
    data,
    totalCount,
  }
}

export function usePaginatedAdvertisements(
  page: number,
  options?: { per?: number; enabled?: boolean }
) {
  const { per, enabled = true } = options ?? {}
  return useQuery({
    queryKey: [
      ...ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENTS_LIST,
      "paginated",
      page,
      per,
    ],
    enabled,
    queryFn: async () => {
      try {
        const params = per != null ? { page, per } : { page }
        return await getAdvertisements(params)
      } catch {
        return {
          data: [],
          meta: {
            current_page: 1,
            next_page: null,
            prev_page: null,
            total_pages: 0,
            total_count: 0,
          },
        }
      }
    },
    staleTime: 30000,
  })
}

export function useAdvertisement(id: string, enabled = true) {
  return useQuery<AdvertisementDetailResponse>({
    queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENT_DETAIL(id),
    queryFn: async () => {
      const response = await getAdvertisementById(id)
      return response
    },
    enabled: enabled && !!id,
    staleTime: 30000,
  })
}

export interface AdvertisementStatsQueryParams {
  from_date?: string
  to_date?: string
}

/**
 * Get advertisement statistics (metrics + sponsorship by day)
 * Uses keepPreviousData so changing date range keeps old data visible during load.
 */
export function useAdvertisementStats(
  id: string,
  params?: AdvertisementStatsQueryParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENT_STATS(
      id,
      params?.from_date,
      params?.to_date
    ),
    queryFn: () => getAdvertisementStats(id, params),
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 30000,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook for creating an advertisement
 */
export function useCreateAdvertisement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateAdvertisementRequest) => {
      return await createAdvertisement(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENTS_LIST,
      })
    },
  })
}

/**
 * Hook for updating an advertisement
 */
export function useUpdateAdvertisement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateAdvertisementRequest
    }) => {
      return await updateAdvertisement(id, data)
    },
    onSuccess: (response, variables) => {
      // Invalidate and refetch advertisements list
      queryClient.invalidateQueries({
        queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENTS_LIST,
      })
      // Update the specific advertisement cache
      queryClient.setQueryData(
        ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENT_DETAIL(variables.id),
        response
      )
    },
  })
}

/**
 * Hook for deleting an advertisement
 */
export function useDeleteAdvertisement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteAdvertisement(id)
      return response
    },
    onSuccess: () => {
      // Invalidate and refetch advertisements list
      queryClient.invalidateQueries({
        queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENTS_LIST,
      })
    },
  })
}
